import { useCallback, useMemo } from 'react';

import { Url } from 'next/dist/shared/lib/router/router';
import { useSearchParams, useParams } from 'next/navigation';

import { TransformedUseCase } from '@repo/editor/src/types';

import { findUseCase } from 'global/utils/useCaseUtils';

import {
  buildTemplatesUrl,
  findActiveTemplateParam,
  getTemplatesCategoryFromUrlParams,
  getTemplatesSubCategoryFromUrlParams,
  getTemplatesUseCaseFromParams,
} from './templateParams';

export const useTemplatesCategory = () => {
  const searchParams = useSearchParams();
  return getTemplatesCategoryFromUrlParams(
    new URLSearchParams(searchParams?.toString())
  );
};

export const useTemplatesCategoryUrl = () => {
  const buildCategoryUrl = useCallback(
    (category: TransformedUseCase): Url => buildTemplatesUrl(category.slug),
    []
  );
  return buildCategoryUrl;
};

export const useTemplatesSubCategory = () => {
  const searchParams = useSearchParams();
  return getTemplatesSubCategoryFromUrlParams(
    new URLSearchParams(searchParams?.toString())
  );
};

export const useTemplatesUseCase = () => {
  const params = useParams();

  return getTemplatesUseCaseFromParams(params).useCase;
};

export const useTemplatesSubUseCase = (
  allCategoriesAndUseCases: TransformedUseCase[]
) => {
  const params = useParams();

  const [useCase, subUseCaseShortName] = params?.useCaseSlug || [];

  // Use usecase data to find the full slug of the sub use case short name
  // We don't use the full slug in the URL for simplicity
  const currentSubUseCaseSlug = useMemo(() => {
    const currentUseCaseData = findUseCase(allCategoriesAndUseCases, useCase);

    const currentSubUseCaseData = currentUseCaseData?.subUseCases?.find(
      (subUseCase) => subUseCase.shortName === subUseCaseShortName
    );

    return currentSubUseCaseData?.slug;
  }, [allCategoriesAndUseCases, subUseCaseShortName, useCase]);

  return currentSubUseCaseSlug;
};

export const useActiveTemplatesParam = (
  allCategoriesAndUseCases: TransformedUseCase[]
) => {
  const currentCategorySlug = useTemplatesCategory();
  const currentSubCategorySlug = useTemplatesSubCategory();
  const currentUseCaseSlug = useTemplatesUseCase();
  const currentSubUseCaseSlug = useTemplatesSubUseCase(
    allCategoriesAndUseCases
  );

  // The most granular non-all filter is the active filter used for the feed.
  return findActiveTemplateParam(
    currentSubUseCaseSlug,
    currentUseCaseSlug,
    currentSubCategorySlug,
    currentCategorySlug
  );
};
